import { BadRequestError, ForbiddenError, HttpError, ValidationFailuresResponse} from "components/ServerValidationErrors";
import { PartitionsApiUrl } from "env-config";
import { acquireAccessToken } from "./access-token-service";

const baseApiUrl = (): string => PartitionsApiUrl().replace(/[/]$/, "");

//#region Type definitions

export type guid = string;

export type QueryConsistencyChecksOptions = {
    fieldCompanyId: number;
    pageToken: string | null;
    itemsPerPage: number | null;
};

export type QueryConsistencyChecksResponse = {
    totalCount: number | null;
    lastUpdatedDate: string;
    items: QueryConsistencyChecksResponseItem[];
};

export type QueryConsistencyChecksResponseItem = {
    id: guid;
    productId: number;
    sku: string;
    stockLedgerQuantity: number;
    warehouseQuantity: number;
    difference: number;
    investigated: boolean;
};

export type UpdateConsistencyCheckRequest = {
    investigated: boolean;
};

//#endregion

async function apiFetch<TResponse>(path: string, options: RequestInit, appendBaseApiUrl = true): Promise<TResponse> {
    const url = appendBaseApiUrl ? `${baseApiUrl()}${path}` : path;
    const accessToken = await acquireAccessToken("api://stock-partition-api", "access");
    const response = await fetch(url, {
        ...options,
        headers: { ...options.headers, Authorization: `Bearer ${accessToken}` },
    });
    if (response.ok) {
        if (response.status === 204) {
            return {} as TResponse;
        }

        const result = await response.json();
        return result as TResponse;
    } else if (response.status === 400) {
        throw new BadRequestError(
            response.status,
            response.statusText,
            (await response.json()) as ValidationFailuresResponse
        );
    } else if (response.status === 403) {
        throw new ForbiddenError(response.status, await response.text());
    } else {
        throw new HttpError(response.status, await response.text());
    }
}

function toQuery(obj: any) {
    return Object.keys(obj)
        .filter((k) => obj[k] !== undefined)
        .filter((k) => obj[k] !== null)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`)
        .join("&");
}

export async function queryConsistencyCheck(options: QueryConsistencyChecksOptions): Promise<QueryConsistencyChecksResponse> {
    return await apiFetch<QueryConsistencyChecksResponse>(`/consistencychecks?${toQuery(options)}`, {
        headers: { "Content-Type": "application/json" },
    });
}

export async function updateConsistencyCheck(consistencyCheckId: guid, request: UpdateConsistencyCheckRequest): Promise<void> {
    await apiFetch<void>(`/consistencychecks/${consistencyCheckId}`, {
        method: "PUT",
        body: JSON.stringify(request),
        headers: { "Content-Type": "application/json" },
    });
}
